class LineItems extends HTMLElement {
  isCartPage = window.location.pathname === `${Shopify.routes.root}cart`;
  section = this.isCartPage ? 'fetch-line-items-cart-page' : 'fetch-line-items';
  minimalSection = this.isCartPage ? 'fetch-line-items-cart-page' : 'fetch-line-items-minimal';

  connectedCallback() {
    this.unsubscribeCartChange = subscribe('cart:change', ({ line, quantity }) =>
      this.cartChange(line, quantity)
    );
    this.unsubscribeRefreshCart = subscribe('cart:added', () => this.refreshCart());
  }

  disconnectedCallback() {
    this.unsubscribeCartChange();
    this.unsubscribeRefreshCart();
  }

  /**
   * Change the cart.
   * @description Use the /{locale}/cart/change.js endpoint to change the quantity, properties, and selling_plan properties of a cart line item. Only items already in your cart can be changed, and you can change only one line item at a time.
   * @tutorial https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(line, quantity) {
    publish('cart:updating', {
      line,
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.section,
      sections_url: window.location.pathname,
    });

    try {
      const { error, data } = await this.fetchCart(routes.cart_change_url, body);

      if (error) throw new Error(error);
      this.renderSections(data.sections);

      publish('cart:updated', {
        itemCount: data.item_count,
      });

      window.toast.success('Cart updated');
    } catch (error) {
      console.error(error);
      window.toast.error(error.message);

      publish('cart:error', {
        line,
      });
    }
  }

  /**
   * Fetch the cart.
   * @param {string} input - The URL to fetch.
   * @param {string} body - The request body.
   * @returns {Promise} The response.
   *
   * @example
   * const json = await this.fetchCart(routes.cart_change_url, body);
   */
  async fetchCart(input, body) {
    const response = await fetch(input, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) return { error: data.errors, data: null };

    return { error: null, data };
  }

  /**
   * Refresh the cart - two-step approach for speed.
   */
  async refreshCart() {
    try {
      // Small delay to ensure Shopify backend has updated cart.items
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 1: Fetch minimal section (just line items) with cache-busting
      const minimalData = await this.fetchSectionWithRetry(this.minimalSection);
      const minimalSections = {};
      minimalSections[this.minimalSection] = minimalData[this.minimalSection];

      // Render minimal section immediately (cart drawer opens with items visible)
      this.renderSections(minimalSections, this.minimalSection);

      // Publish cart updated with item count
      publish('cart:updated', {
        itemCount: minimalData.item_count,
      });

      // Step 2: Fetch full section async in background (recommendations, gifts, USPs)
      if (!this.isCartPage) {
        this.loadFullSectionAsync();
      }
    } catch (error) {
      console.error('Cart refresh failed:', error);
      window.toast.error(error.message);
    }
  }

  /**
   * Fetch section with cache-busting and retry logic
   * Retries if cart appears empty (waiting for server-side update)
   */
  async fetchSectionWithRetry(sectionName, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Add timestamp cache-buster and nonce
        const timestamp = Date.now();
        const url = `${Shopify.routes.root}?sections=${sectionName}&_cb=${timestamp}`;
        const response = await fetch(url, {
          headers: {
            'pragma': 'no-cache',
            'cache-control': 'no-cache'
          }
        });
        const data = await response.json();

        // If we got items or it's not the first attempt, return it
        const html = data[sectionName];
        const hasItems = html && html.includes('line-item');

        if (hasItems || attempt === maxRetries) {
          return data;
        }

        // Wait before retry (incremental backoff)
        await new Promise(resolve => setTimeout(resolve, 50 * attempt));
      } catch (error) {
        if (attempt === maxRetries) throw error;
        // Retry on error
        await new Promise(resolve => setTimeout(resolve, 50 * attempt));
      }
    }
  }

  /**
   * Load full section with recommendations asynchronously
   */
  async loadFullSectionAsync() {
    try {
      const fullResponse = await fetch(`${Shopify.routes.root}?sections=${this.section}`);
      const fullData = await fullResponse.json();
      const fullSections = {};
      fullSections[this.section] = fullData[this.section];

      // Merge full section content into the DOM
      this.renderSections(fullSections, this.section);
    } catch (error) {
      console.error('Failed to load full section:', error);
    }
  }

  /**
   * Render the sections.
   * @param {Object} sections - The JSON response.
   * @param {string} [sectionName] - The section name to render (defaults to this.section)
   * @example
   * this.renderSections(sections);
   * this.renderSections(sections, 'fetch-line-items-minimal');
   */
  renderSections(sections, sectionName = this.section) {
    const html = new DOMParser().parseFromString(sections[sectionName], 'text/html');
    const lineItemsElement = html.querySelector('line-items');

    if (lineItemsElement) {
      if (sectionName === this.minimalSection) {
        // First load: replace entire content with minimal section
        this.innerHTML = lineItemsElement.innerHTML;
      } else {
        // Subsequent load: merge full section (append recommendations, gifts, etc.)
        const childNodes = lineItemsElement.childNodes;
        childNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            // Append recommendations and gifts after the line items
            this.appendChild(node.cloneNode(true));
          }
        });
      }

      // Trigger any custom element upgrades that might be needed
      if (window.customElements) {
        this.querySelectorAll(':not(:defined)').forEach(el => {
          window.customElements.upgrade?.(el);
        });
      }
    }
  }
}

customElements.define('line-items', LineItems);
